import { addToast } from '@app/components/various/Toasts';
import { compiledPaths } from '@app/paths';
import { createAccountFromRequest } from '@app/services/accountRepository';
import { ACCOUNT_REQUEST_DETAILS_QUERY_KEY } from '@app/services/hooks/account/useAccountRequestDetails';
import { ACCOUNT_REQUESTS_QUERY_KEY } from '@app/services/hooks/account/useAccountRequests';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateAccountDTO, CreateAccountSuccessResponse } from '@typings';
import { useTranslation } from 'react-i18next';

interface Params {
  id: string;
  data: CreateAccountDTO;
}

export const useCreateAccountFromRequest = () => {
  const { t } = useTranslation(['accounts', 'common']);
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, data }: Params) => {
    return createAccountFromRequest(id, data);
  };

  return useMutation({
    mutationFn,
    onError: () => {
      addToast(t('accounts:account_create_fail'), { type: 'error' });
    },
    onSuccess: (response: CreateAccountSuccessResponse, { data, id }) => {
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_REQUEST_DETAILS_QUERY_KEY, { id }],
      });
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_REQUESTS_QUERY_KEY],
      });
      addToast(t('accounts:account_created.title', { accountName: data.accountName }), {
        actions: [
          {
            label: t('accounts:view_account'),
            to: compiledPaths.ACCOUNT_DETAILS({ accountId: response.account.account }),
          },
          {
            label: t('common:dismiss'),
          },
        ],
        description: t('accounts:account_created.description'),
        duration: 10000,
      });
    },
  });
};
